import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import {
  Bot,
  Check,
  CheckCircle2,
  CircleHelp,
  Clipboard,
  Code2,
  ExternalLink,
  KeyRound,
  ShieldCheck,
  TerminalSquare,
  Unplug,
} from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SettingsMenuButton, SettingsNavigation } from '@/components/settings/SettingsNavigation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { useBasePath } from '@/lib/paths'
import type { OAuthConnection } from '@/types'

type Props = {
  mcp_url: string
  connections: OAuthConnection[]
}

type CopyButtonProps = {
  value: string
  copyKey: string
  copiedKey: string | null
  onCopy: (value: string, key: string) => void
}

function CopyButton({ value, copyKey, copiedKey, onCopy }: CopyButtonProps) {
  const copied = copiedKey === copyKey

  return (
    <Button variant="outline" size="sm" onClick={() => onCopy(value, copyKey)}>
      {copied ? <Check className="size-4" aria-hidden="true" /> : <Clipboard className="size-4" aria-hidden="true" />}
      {copied ? 'Copied' : 'Copy'}
    </Button>
  )
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-md border border-border bg-muted/40 px-3 py-2 font-mono text-sm text-foreground">
      <code>{children}</code>
    </pre>
  )
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(value))
}

export default function IntegrationsShow({ mcp_url, connections }: Props) {
  const basePath = useBasePath()
  const confirm = useConfirm()
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const codexConfig = `[mcp_servers.corppy]\nurl = "${mcp_url}"`
  const claudeCommand = `claude mcp add --transport http --scope user corppy ${mcp_url}`

  const copy = async (value: string, key: string) => {
    await navigator.clipboard.writeText(value)
    setCopiedKey(key)
    window.setTimeout(() => setCopiedKey((current) => current === key ? null : current), 1600)
  }

  const disconnect = async (connection: OAuthConnection) => {
    const confirmed = await confirm({
      title: `Disconnect ${connection.client_name}?`,
      description: 'This immediately revokes this client’s access to the Corppy account. You can connect it again later.',
      confirmLabel: 'Disconnect',
      destructive: true,
    })

    if (confirmed) {
      router.delete(`${basePath}/settings/integrations/connections/${connection.id}`)
    }
  }

  return (
    <DashboardLayout
      title="AI & integrations"
      navigation={<SettingsNavigation active="integrations" />}
      mobileGuide={<SettingsMenuButton active="integrations" />}
    >
      <Head title="AI & integrations" />

      <div className="space-y-10">
        <header>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight">AI & integrations</h1>
            <Badge variant="outline">MCP 2.0</Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Let an approved AI client help prepare a company application while you keep control of review, signature, and payment.
          </p>
        </header>

        <section aria-labelledby="server-address" className="space-y-4">
          <div>
            <h2 id="server-address" className="text-base font-semibold">Corppy MCP server</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Add this Streamable HTTP address to your AI client. OAuth opens automatically when the client authenticates.
            </p>
          </div>
          <div className="flex flex-col gap-2 rounded-lg border border-border bg-muted/20 p-4 sm:flex-row sm:items-center">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Server URL</p>
              <code className="mt-1 block truncate font-mono text-sm text-foreground">{mcp_url}</code>
            </div>
            <CopyButton value={mcp_url} copyKey="server-url" copiedKey={copiedKey} onCopy={copy} />
          </div>
          <div className="flex gap-3 rounded-lg border border-border px-4 py-3">
            <ShieldCheck className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden="true" />
            <div>
              <p className="text-sm font-medium text-foreground">Approved clients only during early access</p>
              <p className="mt-0.5 text-sm text-muted-foreground">
                Corppy reviews each client’s OAuth callback before it can connect. Ask Corppy to approve your client if authentication reports an unknown client.
              </p>
            </div>
          </div>
        </section>

        <Separator />

        <section aria-labelledby="setup-guide" className="space-y-4">
          <div>
            <h2 id="setup-guide" className="text-base font-semibold">Connect your AI client</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose the client you use. You will sign in to Corppy and approve the exact account and permissions before access starts.
            </p>
          </div>

          <Tabs defaultValue="chatgpt" orientation="vertical" className="flex-col gap-4 md:grid md:grid-cols-[12rem_minmax(0,1fr)]">
            <TabsList className="h-auto w-full flex-col items-stretch justify-start bg-muted/50 p-1">
              <TabsTrigger value="chatgpt" className="justify-start">
                <Bot className="size-4" aria-hidden="true" /> ChatGPT desktop
              </TabsTrigger>
              <TabsTrigger value="codex" className="justify-start">
                <TerminalSquare className="size-4" aria-hidden="true" /> Codex
              </TabsTrigger>
              <TabsTrigger value="claude" className="justify-start">
                <Code2 className="size-4" aria-hidden="true" /> Claude Code
              </TabsTrigger>
              <TabsTrigger value="other" className="justify-start">
                <CircleHelp className="size-4" aria-hidden="true" /> Other client
              </TabsTrigger>
            </TabsList>

            <div className="min-w-0 rounded-lg border border-border p-5">
              <TabsContent value="chatgpt" className="space-y-4">
                <div>
                  <h3 className="font-medium text-foreground">ChatGPT desktop</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Use the built-in Streamable HTTP server setup.</p>
                </div>
                <ol className="list-decimal space-y-2 pl-5 text-sm text-foreground marker:text-muted-foreground">
                  <li>Open <strong>Settings</strong>, then <strong>MCP servers</strong>.</li>
                  <li>Select <strong>Add server</strong>, choose <strong>Streamable HTTP</strong>, and paste the Corppy server URL.</li>
                  <li>Save, restart the client, then select <strong>Authenticate</strong> beside Corppy.</li>
                  <li>Choose the Corppy account and review the requested permissions in the browser.</li>
                </ol>
              </TabsContent>

              <TabsContent value="codex" className="space-y-4">
                <div>
                  <h3 className="font-medium text-foreground">Codex CLI, app, or IDE</h3>
                  <p className="mt-1 text-sm text-muted-foreground">These clients share the same Codex MCP configuration.</p>
                </div>
                <CodeBlock>{codexConfig}</CodeBlock>
                <div className="flex flex-wrap gap-2">
                  <CopyButton value={codexConfig} copyKey="codex-config" copiedKey={copiedKey} onCopy={copy} />
                </div>
                <p className="text-sm text-muted-foreground">
                  Add that block to <code className="font-mono text-foreground">~/.codex/config.toml</code>, restart Codex, then run <code className="font-mono text-foreground">codex mcp login corppy</code>.
                </p>
              </TabsContent>

              <TabsContent value="claude" className="space-y-4">
                <div>
                  <h3 className="font-medium text-foreground">Claude Code</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Add Corppy for your user, then authenticate from Claude Code.</p>
                </div>
                <CodeBlock>{claudeCommand}</CodeBlock>
                <CopyButton value={claudeCommand} copyKey="claude-command" copiedKey={copiedKey} onCopy={copy} />
                <p className="text-sm text-muted-foreground">
                  Start Claude Code, enter <code className="font-mono text-foreground">/mcp</code>, select Corppy, and follow the browser sign-in.
                </p>
              </TabsContent>

              <TabsContent value="other" className="space-y-4">
                <div>
                  <h3 className="font-medium text-foreground">Another MCP client</h3>
                  <p className="mt-1 text-sm text-muted-foreground">Use these connection settings in any compatible client.</p>
                </div>
                <dl className="grid gap-3 text-sm sm:grid-cols-[9rem_1fr]">
                  <dt className="text-muted-foreground">Transport</dt><dd className="font-medium">Streamable HTTP</dd>
                  <dt className="text-muted-foreground">Authentication</dt><dd className="font-medium">OAuth 2.1 with PKCE</dd>
                  <dt className="text-muted-foreground">Server</dt><dd className="break-all font-mono">{mcp_url}</dd>
                </dl>
                <p className="text-sm text-muted-foreground">
                  The client ID and exact callback URL must be approved by Corppy before the OAuth flow begins.
                </p>
              </TabsContent>
            </div>
          </Tabs>
        </section>

        <Separator />

        <section aria-labelledby="connected-clients" className="space-y-4">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 id="connected-clients" className="text-base font-semibold">Connected clients</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Review and revoke the AI clients that can act in this Corppy account.
              </p>
            </div>
            {connections.length > 0 && <Badge variant="secondary">{connections.length}</Badge>}
          </div>

          {connections.length === 0 ? (
            <div className="flex flex-col items-center rounded-lg border border-dashed border-border px-6 py-10 text-center">
              <KeyRound className="size-8 text-muted-foreground" aria-hidden="true" />
              <h3 className="mt-3 text-sm font-medium text-foreground">No AI clients connected</h3>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                Complete OAuth from an approved client and it will appear here immediately.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-border rounded-lg border border-border">
              {connections.map((connection) => (
                <article key={connection.id} className="p-4 transition-colors hover:bg-muted/20">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-medium text-foreground">{connection.client_name}</h3>
                        <Badge variant="outline" className="text-primary">
                          <CheckCircle2 className="size-3" aria-hidden="true" /> Connected
                        </Badge>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        Connected by {connection.connected_by} on {formatDate(connection.created_at)}
                        {connection.expires_at ? ` · Current token expires ${formatDate(connection.expires_at)}` : ''}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => disconnect(connection)}>
                      <Unplug className="size-4" aria-hidden="true" /> Disconnect
                    </Button>
                  </div>

                  <details className="mt-4 rounded-md bg-muted/30 px-3 py-2">
                    <summary className="cursor-pointer text-sm font-medium text-foreground">
                      Permissions ({connection.permissions.length})
                    </summary>
                    <ul className="mt-3 grid gap-3 sm:grid-cols-2">
                      {connection.permissions.map((permission) => (
                        <li key={permission.key} className="flex gap-2 text-sm">
                          <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                          <span>
                            <span className="block font-medium text-foreground">{permission.title}</span>
                            <span className="text-muted-foreground">{permission.description}</span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                </article>
              ))}
            </div>
          )}

          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <ExternalLink className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            Signature and Stripe checkout always open as separate human-controlled steps. Disconnecting a client does not cancel an existing order.
          </p>
        </section>
      </div>
    </DashboardLayout>
  )
}
